import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  VueForgotCredentialsService,
  VueFormError,
  VuePasswordStrengthInputComponent,
  VueSecurityQuestionAnswer,
  VueServerMessage, VueServerMessageType, VueTranslationService,
  VueValidationRules
} from '@vue/vueframework';
import { AccountManagementService } from '../shared/services/account-management.service';

/**
 * This component is for a user to change
 * their password
 *
 * It is normally placed on a route
 * path: 'changePassword/:userID/:hash'
 *
 * and it made to communicate with the login route
 * path: 'authenticate
 *
 *
 */
@Component({
  selector: 'vue-change-password',
  templateUrl: './change-password-recovery.component.html',
  standalone: false
})
export class ChangePasswordRecoveryComponent implements OnInit {
  /**
     * Vue Component that handles password validation
     */
  @ViewChild('passwordInputContainer') public passwordInputContainer: VuePasswordStrengthInputComponent;

  /**
   * Standard vue form error
   */
  public formError = new VueFormError();

  /**
   * Page level messages displayed to the user
   */
  public messages: VueServerMessage[] = [];

  /**
   * Required rules for the password strength.
   * We currently use default optional rules.
   */
  public requiredRules: VueValidationRules = {
    rules: [
      {
        class: 'validation-disabled',
        label: '',
        labelKey: 'passwordRule.label.minimumLength',
        labelParams: [ 12 ],
        order: 1,
        regex: new RegExp('^.{12,}$')
      }
    ]
  };

  /**
   * This holds the answers to the security questions provided
   * by the user
   */
  public securityQuestionAnswers: VueSecurityQuestionAnswer[] = [];

  public verified: boolean;

  public isQuestionSelected: boolean;

  private hash: string;

  private userID: string;

  public constructor(
    private accountService: AccountManagementService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private vueTranslationService: VueTranslationService,
    private forgotCreds: VueForgotCredentialsService
  ) {
  }

  /**
   * Runs whenever we retrieve security questions
   * or activate the user
   * @param response HttpErrorResponse
   */
  public apiFailure(response: HttpErrorResponse): void {
    const errorMessage = this.vueTranslationService.getTranslationFromServerCode(
      response.error[ 0 ]
    );
    this.messages = [ new VueServerMessage(VueServerMessageType.Error, errorMessage, false, null, false, true, true) ];
  }

  public isPasswordInputContainerValid(): boolean {
    return this.passwordInputContainer.passwordResetForm.valid &&
      this.passwordInputContainer.passwordInput.isStrongPassword &&
      this.passwordInputContainer.passwordsMatch;
  }

  /**
   * Calls service to change password once user
   * inputs a strong password
   */
  public changePassword(): void {
    this.messages = [];
    this.formError.invalidFormSubmit = false;
    if (this.isPasswordInputContainerValid()) {
      this.accountService
        .forgotPasswordUpdatePassword(
          this.userID,
          this.hash,
          this.passwordInputContainer.passwordInput.newPassword,
          this.securityQuestionAnswers)
        .subscribe({
          error: (err: HttpErrorResponse) => this.apiFailure(err),
          next: (result) => this.changePasswordSuccess(result)
        });
    } else {
      this.formError.invalidFormSubmit = true;
    }
  }

  /**
   * Navigates back to the Login Page with success message
   * @param siteID site id value fetched from state or response of change password
   */
  public changePasswordSuccess(siteID: string): void {
    const message = this.vueTranslationService
      .getTranslation('Connect', 'changePassword.successMessageLoginPage', [ siteID ]);
    this.router.navigate([ '/authenticate' ], {
      state: {
        message: new VueServerMessage(VueServerMessageType.Success, message)
      }
    });
  }

  public ngOnInit(): void {
    this.verified = false;
    this.isQuestionSelected = false;
    const { userID, hash } = this.activatedRoute.snapshot.params;
    this.userID = userID;
    this.hash = hash;

    if (history.state) {
      if (history.state.securityQuestionAnswers) {
        this.securityQuestionAnswers = history.state.securityQuestionAnswers;
        this.isQuestionSelected = true;
      }
      if (history.state.appUserID) {
        this.userID = history.state.appUserID;
      }
    }
    if (this.userID && (this.hash || this.securityQuestionAnswers.length > 0)) {
      this.verified = true;
    }
  }

  /**
     * Navigate to forgot password
     */
  public forgotPassword(): void {
    this.forgotCreds.forgotPassword();
  }
}
